<script lang="ts">
import { defineComponent } from 'vue'
export default defineComponent({
  name: 'TMapGeofenceDrawer',
})
</script>
<template>
  <div :class="bem()">
    <Spin :spinning="loading">
      <div :class="bem('ins')" ref="mapRef" :style="{ height }" />
    </Spin>
    <div :class="bem('ctl')">
      <BdBtn :icon="GatewayOutlined" type="primary" @click="handleStartDraw" :disabled="isCreating">新建绘制</BdBtn>
      <AddressSearch @search="searchAddress" @select="handleAddressSelect" :options="searchOptions" />
    </div>
    <div :class="bem('ft')">
      <GeofenceArea
        v-for="(_, index) in paths"
        :key="index"
        :index="index"
        :editing="editIndex === index"
        @edit="handleEditPolygon(index)"
        @del="handleDeletePolygon(index)"
        @closeEdit="handleFinishEdit(index)"
        @click="handleZoomToPolygon(index)"
      />
    </div>
  </div>
</template>

<script lang="ts" setup name="TMapGeofenceDrawer">
import { computed, onMounted, ref, nextTick, toRef, watch } from 'vue'
import { Spin } from 'ant-design-vue'
import { GatewayOutlined } from '@ant-design/icons-vue'
import { createNamespace, isNumber } from '@bd/vue3-utils'
import { BdBtn } from '-/Common/BdBtn'
import { RS_CENTER_LNG_LAT } from '@/constant'
import GeofenceArea from './Area.vue'
import AddressSearch from '../AddressSearch.vue'
import { useTMapBase } from '../useTMapBase'
import { useTMapLocalSearch } from '../useTMapLocalSearch'
import { useTMapGeofence } from '../useTMapGeofence'

const [_, bem] = createNamespace('TMapGeofenceDrawer')

/** 组件属性 */
const props = withDefaults(
  defineProps<{
    /** 地图高度 */
    height?: string
    /** 围栏数据（JSON格式的多边形数组） */
    value?: string
  }>(),
  {
    height: '400px'
  }
)

/** 组件事件 */
const emits = defineEmits<{
  (e: 'update:value', value?: string): void
}>()

const mapRef = ref<HTMLElement>()
const loading = ref(false)
const T = window.T

// 使用基础地图 Hook
const { Map, initMap } = useTMapBase(mapRef, {
  center: RS_CENTER_LNG_LAT
})

// 使用本地搜索 Hook
const { options: searchOptions, initLocalSearch, search: searchAddress } = useTMapLocalSearch(Map)

// 使用地理围栏管理 Hook
const valueRef = toRef(props, 'value')
const { paths, editIndex, syncPolygons, startEdit, finishEdit, deletePolygon, addPolygon, zoomToPolygon, zoomToFit, initDrawTool } = useTMapGeofence(
  Map,
  valueRef
)

/** 多边形绘制工具实例 */
let polygonTool: any = null

/** 是否正在创建新围栏 */
const isCreating = computed(() => isNumber(editIndex.value))

/**
 * 开始绘制新围栏
 */
const handleStartDraw = () => {
  if (polygonTool) {
    polygonTool.open()
  }
}

/**
 * 绘制完成回调
 * @param points - 新绘制的多边形顶点坐标
 */
const handleDrawComplete = (points: LngLatPoint[]) => {
  const newValue = addPolygon(points)
  emits('update:value', newValue)
  if (polygonTool) {
    polygonTool.clear()
  }
}

/**
 * 地址选择处理
 * 选中搜索结果后地图居中到该位置
 */
const handleAddressSelect = (_: any, option: any) => {
  if (!Map.value) return
  const { lnglat } = option
  const point = new T.LngLat(Number(lnglat[0]), Number(lnglat[1]))
  Map.value.centerAndZoom(point, 18)
}

/**
 * 地图居中到指定围栏
 */
const handleZoomToPolygon = (index: number) => {
  zoomToPolygon(index)
}

/**
 * 开始编辑围栏
 */
const handleEditPolygon = (index: number) => {
  startEdit(index)
}

/**
 * 完成编辑围栏
 */
const handleFinishEdit = (index: number) => {
  const newValue = finishEdit(index)
  emits('update:value', newValue)
}

/**
 * 删除围栏
 */
const handleDeletePolygon = (index: number) => {
  const newValue = deletePolygon(index)
  emits('update:value', newValue)
}

/**
 * 初始化围栏数据同步
 * 首次加载和数据变化时同步多边形到地图
 */
let lastPathsLength = 0
let isFirstSync = true

const handlePathsChange = () => {
  syncPolygons(paths.value)

  // 只在首次加载或数量变化时自动调整视野
  if (isFirstSync || lastPathsLength !== paths.value.length) {
    zoomToFit()
  }

  isFirstSync = false
  lastPathsLength = paths.value.length
}

// 监听围栏数据变化
watch(() => props.value, handlePathsChange)

onMounted(() => {
  nextTick(() => {
    const mapIns = initMap()
    if (mapIns) {
      // 初始化多边形绘制工具
      polygonTool = initDrawTool(handleDrawComplete)

      // 初始化搜索功能
      initLocalSearch()

      // 初始化围栏数据显示
      handlePathsChange()
    }
  })
})
</script>
<style lang="less">
@import '../index.less';

.t-map-geofence-drawer {
  .map-box();

  &__ft {
    display: flex;
    gap: @margin-sm;
    margin-top: @margin-xs;
  }

  &__ctl {
    .map-box-ctl();
  }
}
</style>
